import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Film, Play, Video } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

type EducationalVideo = {
  id: string;
  title: string;
  slug: string;
  description: string;
  video_url: string;
  thumbnail_url: string | null;
  category: string;
  video_type: 'video';
  featured: boolean;
};

const VIDEO_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/manage-videos`;

export const VideoPreview = () => {
  const [videos, setVideos] = useState<EducationalVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref, inView } = useInView(0.1);

  useEffect(() => {
    fetch(`${VIDEO_URL}?action=public`)
      .then(res => res.json())
      .then(data => setVideos((Array.isArray(data) ? data : []).slice(0, 3)))
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && videos.length === 0) return null;

  const getYouTubeId = (url: string) => {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([a-zA-Z0-9_-]{6,})/);
    return match?.[1] ?? null;
  };

  return (
    <section id="videos" className="py-20 sm:py-24 md:py-28 section-alt" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-10 sm:mb-12 md:mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Film size={14} className="text-primary" />
            <span className="text-[10px] sm:text-xs font-bold text-primary uppercase tracking-[0.2em]">Video Library</span>
          </div>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Educational Videos
          </h2>
          <div className="w-12 h-1 bg-accent rounded-full mx-auto mb-4" />
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto px-4">Practical clinical education, healthcare insights, and patient-friendly learning resources.</p>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[1, 2, 3].map(item => <div key={item} className="aspect-[9/16] animate-pulse rounded-2xl bg-muted" />)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {videos.map((video, index) => (
              <Link key={video.id} to={`/videos#${video.slug}`} className={`group overflow-hidden rounded-2xl border border-border/30 bg-card/80 shadow-card transition-all duration-500 hover:-translate-y-2 hover:shadow-glow ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${index * 100 + 160}ms` }}>
                <div className="relative aspect-[9/16] overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10">
                  {video.thumbnail_url ? <img src={video.thumbnail_url} alt={video.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="flex h-full items-center justify-center"><Play size={34} className="text-primary/50" /></div>}
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/75 via-foreground/10 to-transparent" />
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-background/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-primary backdrop-blur-md"><Video size={11} />Video</span>
                  <span className="absolute left-1/2 top-1/2 inline-flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 text-primary shadow-elevated backdrop-blur-md transition-transform group-hover:scale-105"><Play size={22} className="ml-1" /></span>
                  {getYouTubeId(video.video_url) && <span className="absolute bottom-4 left-4 rounded-full bg-primary/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">YouTube</span>}
                </div>
                <div className="p-6">
                  <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">{video.category}</p>
                  <h3 className="line-clamp-2 text-base font-bold text-foreground transition-colors group-hover:text-primary">{video.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{video.description}</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className={`text-center mt-10 transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Link to="/videos" className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold text-primary-foreground bg-primary rounded-xl hover:bg-primary/90 shadow-card hover:shadow-glow transition-all group">
            View Video Library <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};
